import { settings } from '@/config';

/**
 * Film grain + vignette as a DOM overlay ON TOP of the canvas (2026-07-18, user: cinematic
 * noise "in front of camera" + a smooth vignette). Deliberately NOT a WebGL post pass — the
 * composer is off because its OutputPass re-tone-maps the baked atlases, so a screen-space DOM
 * layer keeps the baked look untouched, costs almost nothing, and reads exactly as "in front of
 * the camera".
 *
 *  - GRAIN: a small procedural noise tile, repeated, nudged by a GPU-composited `transform`
 *    every ~1/fps s so it scintillates like real film grain, blended `overlay` at low opacity.
 *  - VIGNETTE: a radial gradient darkening the frame edges, `strength`/`softness` tunable.
 *
 * Both are `pointer-events:none`, so they never intercept scroll/hover. Layered just above the
 * canvas but below the debug HUDs so those stay legible.
 */
export class CinematicOverlay {
  private readonly root: HTMLDivElement;
  private readonly vignetteEl: HTMLDivElement;
  private readonly grainEl: HTMLDivElement;
  /** The circular-rainbow lens ring — see {@link applyRainbow}. */
  private readonly rainbowEl: HTMLDivElement;
  private accumulator = 0;
  private noiseUrl = '';

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'cinematic-overlay';
    this.root.style.cssText =
      'position:fixed;inset:0;z-index:6;pointer-events:none;overflow:hidden';

    this.vignetteEl = document.createElement('div');
    this.vignetteEl.style.cssText = 'position:absolute;inset:0;pointer-events:none';

    this.grainEl = document.createElement('div');
    // 130% so the transform nudges never expose an edge; mix-blend overlay = filmic.
    this.grainEl.style.cssText = [
      'position:absolute',
      'top:-15%',
      'left:-15%',
      'width:130%',
      'height:130%',
      'pointer-events:none',
      'mix-blend-mode:overlay',
      'will-change:transform',
      'background-repeat:repeat',
    ].join(';');

    // RAINBOW RING (2026-07-20, user: "like after rain … a circle rainbow, just very very
    // subtle"). Below the vignette/grain so they still grade over it.
    this.rainbowEl = document.createElement('div');
    this.rainbowEl.style.cssText = [
      'position:absolute',
      'pointer-events:none',
      'border-radius:50%',
      'mix-blend-mode:screen', // additive-ish: only ever brightens, never muddies the scene
      'will-change:transform',
      // Slow opacity transition — the CHAPTER-3 fade in/out rides this (2026-07-21, user:
      // "very smooth, the user will not even get it").
      'transition:opacity 2400ms ease',
    ].join(';');

    this.root.append(this.rainbowEl, this.vignetteEl, this.grainEl);
    document.body.appendChild(this.root);

    this.noiseUrl = this.buildNoiseTile();
    this.applyVignette();
    this.applyGrain();
    this.applyRainbow();
    window.addEventListener('resize', this.onResize);
  }

  private readonly onResize = (): void => this.applyRainbow();

  /**
   * The circular-rainbow lens effect: a full spectral sweep (conic gradient) masked to a thin
   * ring by a radial-gradient mask, heavily blurred and screen-blended so it reads as a faint
   * atmospheric halo on the lens — not a sticker. Pure DOM/CSS: zero GPU-pass cost, and every
   * parameter (size/thickness/blur/opacity/position) is live from the GUI.
   */
  /** 0..1 factor from the CHAPTER gate — the ring belongs to Chapter 3 only (2026-07-21).
   *  Starts hidden; {@link setRainbowVisible} flips it and the CSS transition does the fade. */
  private rainbowSectionFactor = 0;

  /**
   * Chapter gate for the ring: fades in as Chapter 3 begins, fades out as it ends — driven from
   * `Experience` on every section change. Only the target flips here; the 2.4s CSS opacity
   * transition on the element carries the actual fade, so it's identical however the section
   * was reached (scroll, magnet, checkpoint, restart).
   */
  setRainbowVisible(on: boolean): void {
    this.rainbowSectionFactor = on ? 1 : 0;
    this.applyRainbow();
  }

  /** STORY-MODE mute (2026-09-02, user: "when shader mode is on I don't want to see that
   *  rainbow"): a daylight lens artefact has no place in the black-and-ember story world, so
   *  the ring fades out with the smoke whenever a story is open — independent of the chapter
   *  gate, which is restored untouched on close. The same 2.4s CSS transition carries it. */
  private rainbowStoryMuted = false;

  setRainbowMuted(muted: boolean): void {
    this.rainbowStoryMuted = muted;
    this.applyRainbow();
  }

  /** FRAME GATE (2026-09-03, user: "at frame 170 fade out that rainbow"): Experience drives
   *  this every tick from the CAMERA's clip frame, so the ring leaves partway through
   *  Chapter 3 — before the chapter itself ends — and returns if the user scrubs back.
   *  Same-state calls are no-ops; the 2.4s CSS transition carries the fade. */
  private rainbowFrameGate = true;

  setRainbowFrameGate(on: boolean): void {
    if (on === this.rainbowFrameGate) return;
    this.rainbowFrameGate = on;
    this.applyRainbow();
  }

  applyRainbow(): void {
    const r = settings.cinematic.rainbow;
    const el = this.rainbowEl;
    if (!r.enabled) {
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    const vmin = Math.min(window.innerWidth, window.innerHeight);
    const d = Math.round(vmin * r.size);
    // Ring mask: transparent centre -> opaque band -> transparent edge. Thickness is a fraction
    // of the radius; the band is centred just inside the rim so the blur never clips.
    const inner = Math.max(0, Math.round((1 - r.thickness) * 88));
    el.style.width = `${d}px`;
    el.style.height = `${d}px`;
    el.style.left = `calc(${(r.x * 100).toFixed(1)}% - ${d / 2}px)`;
    el.style.top = `calc(${(r.y * 100).toFixed(1)}% - ${d / 2}px)`;
    el.style.opacity = String(
      r.opacity *
        this.rainbowSectionFactor *
        (this.rainbowStoryMuted ? 0 : 1) *
        (this.rainbowFrameGate ? 1 : 0),
    );
    el.style.filter = `blur(${r.blur}px)`;
    el.style.background =
      'conic-gradient(#ff5e5e,#ffb45e,#f6f27a,#7ce87c,#6fd3ff,#7a8cff,#c07aff,#ff5e5e)';
    const mask = `radial-gradient(circle, transparent ${inner - 12}%, black ${inner}%, black ${inner + 8}%, transparent ${inner + 20}%)`;
    el.style.webkitMaskImage = mask;
    el.style.maskImage = mask;
  }

  /** A 96² grayscale noise PNG as a data-URI — the repeated grain cell. */
  private buildNoiseTile(): string {
    const size = 96;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    const img = ctx.createImageData(size, size);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /** Push the current vignette settings (radial gradient). */
  applyVignette(): void {
    const v = settings.cinematic.vignette;
    if (!v.enabled) {
      this.vignetteEl.style.background = 'none';
      return;
    }
    // `softness` pushes the transparent inner stop outward; `strength` sets the edge darkness.
    const inner = Math.round(20 + v.softness * 55); // 20-75%
    const rgb = hexToRgb(v.color);
    this.vignetteEl.style.background =
      `radial-gradient(ellipse at center, rgba(${rgb},0) ${inner}%, rgba(${rgb},${v.strength}) 100%)`;
  }

  /** Push the current grain settings (tile, opacity, scale). */
  applyGrain(): void {
    const g = settings.cinematic.grain;
    this.grainEl.style.opacity = g.enabled ? String(g.opacity) : '0';
    this.grainEl.style.backgroundImage = `url(${this.noiseUrl})`;
    const px = Math.max(1, Math.round(96 * g.scale));
    this.grainEl.style.backgroundSize = `${px}px ${px}px`;
    if (!g.animate) this.grainEl.style.transform = 'translate3d(0,0,0)'; // fixed / static
  }

  /** Scintillate the grain — only when `animate` is on. Static (fixed) otherwise. */
  update(delta: number): void {
    const g = settings.cinematic.grain;
    if (!g.enabled || g.opacity <= 0 || !g.animate) return;
    this.accumulator += delta;
    if (this.accumulator < 1 / Math.max(g.fps, 1)) return;
    this.accumulator = 0;
    const jitter = 6;
    const x = (Math.random() * 2 - 1) * jitter;
    const y = (Math.random() * 2 - 1) * jitter;
    this.grainEl.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
  }

  setVisible(visible: boolean): void {
    this.root.style.display = visible ? '' : 'none';
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.root.remove();
  }
}

/** '#RRGGBB' → 'r,g,b'. */
function hexToRgb(hex: string): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}
